import STAGES, {
  StageEvent,
  StageData,
  DEFAULT_CAMEL_CARAVAN_EVENT,
} from "../consts/STAGES";
import DestructibleType from "../enums/DestructibleType";
import EnemyType from "../enums/EnemyType";
import StageType from "../enums/StageType";
import EnemyGroup from "./enemy/EnemyGroup";
import Game from "./Game";
import StageEventManager from "./StageEventManager";
import { FormationConfig } from "../game/formations/BaseFormation";
import { getCurrentPerformanceConfig } from "../../utils/performance";

// Interface for boss pools with spawn count tracking
interface BossPool extends EnemyGroup {
  spawnCount: number;
}

export default class Stage {
  stageEventManager: StageEventManager;
  levelName: string;
  description: string;
  minute: number;
  frequency: number;
  pause: number;
  startingSpawns: number;
  minimum: number;
  maximum: number;
  enemies: EnemyType[];
  bosses: any[];
  BGTextureName: string;
  minimumMultiplier: number;
  maxTreasureLuck: number;
  destructibleFreq: number;
  destructibleChance: number;
  destructibleChanceMax: number;
  maxDestructibles: number;
  pools: EnemyGroup[];
  bossPools: BossPool[];
  hasAttachedTreasure: boolean;
  hasTileset: boolean;
  scene: Phaser.Scene;
  levelType: StageType;
  rectOuter: Phaser.Geom.Rectangle;
  rectInner: Phaser.Geom.Rectangle;
  events: StageEvent[];
  treasure: null;
  SpawnTimer: Phaser.Time.TimerEvent;
  DestructibleTimer: Phaser.Time.TimerEvent;
  destructibleType: DestructibleType;

  // Cache the stage data array for better performance
  private readonly stageDataArray: StageData[];

  constructor(scene: Phaser.Scene, stage: StageType) {
    this.levelName = "";
    this.description = "";
    this.minute = 0;
    this.frequency = 1000;
    this.pause = 0;
    this.startingSpawns = 0;
    this.minimum = 0;
    this.maximum = 60;
    this.destructibleType = DestructibleType.GIFT;
    this.enemies = new Array();
    this.bosses = new Array();
    this.BGTextureName = "";
    this.minimumMultiplier = 1;
    this.maxTreasureLuck = 20;
    this.destructibleFreq = 10000;
    this.destructibleChance = 5;
    this.destructibleChanceMax = 50;
    this.maxDestructibles = 10;
    this.pools = new Array();
    this.bossPools = new Array();
    this.hasAttachedTreasure = false;
    this.hasTileset = false;
    this.scene = scene;
    this.levelType = stage;
    this.stageEventManager = new StageEventManager(scene, this);

    // Cache the stage data array once in constructor
    this.stageDataArray = STAGES[this.levelType];
  }
  Init() {
    this.minute = 0;
    const stageData = this.stageDataArray[this.minute];
    this.UpdateData(stageData);

    this.rectOuter = new Phaser.Geom.Rectangle(
      -0.5 * this.scene.renderer.width - 150,
      -0.5 * this.scene.renderer.height - 150,
      this.scene.renderer.width + 200,
      this.scene.renderer.height + 200
    );
    this.rectInner = new Phaser.Geom.Rectangle(
      -0.5 * this.scene.renderer.width - 75,
      -0.5 * this.scene.renderer.height - 75,
      this.scene.renderer.width + 100,
      this.scene.renderer.height + 100
    );
    // TODO: Scene bonus
    for (let e = 0; e < this.startingSpawns; e++) {
      this.SpawnEnemiesInOuterRect();
    }
  }

  CheckMinute() {
    // Calculate current minute including half minutes
    const currentMinute = Math.floor(Game.Core.SurvivedSeconds / 30) / 2;

    // Always spawn caravan every 0.5 minutes (at each minute/half-minute interval)
    this.spawnCaravanEvent();

    // Find stage data for this minute/half-minute
    const stageData = this.stageDataArray.find(
      (stage) => stage.minute === currentMinute
    );

    if (stageData) {
      console.log(
        `CheckMinute: Found stage data for minute ${currentMinute}`,
        stageData.bosses
      );
      this.minute = currentMinute;
      // Apply the stage data for this minute/half-minute
      this.UpdateData(stageData);

      // Reset flags and spawn boss if needed
      this.hasAttachedTreasure = false;
      this.SpawnBoss();
    } else {
      console.log(
        `CheckMinute: No stage data found for minute ${currentMinute}`
      );
    }
  }
  SpawnBoss() {
    console.log(`SpawnBoss called with ${this.bossPools.length} boss pools`);
    for (let t = 0; t < this.bossPools.length; t++) {
      const pool = this.bossPools[t];
      if (!pool.enabled) {
        console.log(`Pool ${t} (${pool.enemyType}) is disabled, skipping`);
        continue;
      }

      // Get spawn count for this pool (defaults to 1 for backward compatibility)
      const spawnCount = pool.spawnCount || 1;
      console.log(`Spawning ${spawnCount} bosses of type ${pool.enemyType}`);

      // Spawn multiple bosses if needed
      for (let spawnIndex = 0; spawnIndex < spawnCount; spawnIndex++) {
        console.log(
          `Spawning boss ${spawnIndex + 1}/${spawnCount} of type ${
            pool.enemyType
          }`
        );
        // Generate random position for each boss spawn
        let i = 2 * Math.PI * Math.random(),
          s = 256 * Math.random(),
          n =
            Game.Core.Player.x +
            0.9 * Math.cos(i) * (this.scene.renderer.width + s),
          a =
            Game.Core.Player.y +
            0.9 * Math.sin(i) * (this.scene.renderer.height + s);

        var e = pool.SpawnAt(n, a);
        console.log(
          `Boss spawned successfully:`,
          e ? `ID: ${e.enemyType}` : "FAILED TO SPAWN"
        );

        // Pop boss ID when spawning boss, not when treasure is picked up
        const bossIdAndValue = Game.Core.StageService.popBossId();
        if (bossIdAndValue) {
          e.setBossId(bossIdAndValue);
        }

        (e.isTeleportOnCull = true),
          !this.hasAttachedTreasure &&
            this.treasure &&
            this.SetTreasureLevelFromChance(this.treasure) > 0 &&
            (this.hasAttachedTreasure = true);
        // Note: AttachTreasure method may need to be implemented on Enemy class
      }
    }
  }
  SetTreasureLevelFromChance(e: any) {
    var t,
      i = null === (t = Game.Core.Player) || void 0 === t ? void 0 : t.luck;
    return (
      void 0 === i && (i = 1),
      100 * Math.random() <= e.chances[0] * i
        ? ((e.level = 3), 3)
        : 100 * Math.random() <= e.chances[1] * i
        ? ((e.level = 2), 2)
        : 100 * Math.random() <= e.chances[2] * i
        ? ((e.level = 1), 1)
        : 0
    );
  }

  UpdateData(stageData) {
    this.events = [];
    this.bosses = [];
    this.treasure = null;
    for (const key in stageData) {
      if (this.hasOwnProperty(key)) {
        this[key] = stageData[key];
      }
    }
    console.log("UpdateData: this.bosses after update:", this.bosses);
    this.updateEnemyPools();
    this.updateTimers();
    this.playEvents();
    if (stageData.formations) {
      this.spawnFormations(stageData.formations);
    }
  }
  playEvents() {
    for (let i = 0, len = this.events.length; i < len; i++) {
      this.stageEventManager.TriggerEvent(this.events[i]);
    }
  }
  updateTimers() {
    if (this.pause) {
      Game.Core.scene.time.addEvent({
        delay: this.pause,
        loop: false,
        callback: () => {
          this.startTimers();
        },
      });
    } else {
      this.startTimers();
    }
  }
  startTimers() {
    Game.Core.scene.time.removeEvent(this.SpawnTimer);
    this.SpawnTimer = Game.Core.scene.time.addEvent({
      delay: this.frequency,
      loop: true,
      callback: () => {
        this.SpawnEnemiesInOuterRect();
      },
    });
    Game.Core.scene.time.removeEvent(this.DestructibleTimer);
    this.DestructibleTimer = Game.Core.scene.time.addEvent({
      delay: this.destructibleFreq,
      loop: true,
      callback: () => {
        this.SpawnDestructibleOutOfSight();
      },
    });
  }
  SpawnDestructibleOutOfSight() {
    if (
      100 * Math.random() <=
      Math.min(
        this.destructibleChance * Game.Core.Player.luck,
        this.destructibleChanceMax
      )
    ) {
      Game.Core.MakeDestructible(this.destructibleType);
    }
  }
  updateEnemyPools() {
    for (let i = 0, len = this.pools.length; i < len; i++) {
      this.pools[i].enabled = false;
    }
    for (let i = 0, len = this.bossPools.length; i < len; i++) {
      this.bossPools[i].enabled = false;
      // Reset spawn count for existing pools
      this.bossPools[i].spawnCount = 0;
    }
    for (let i = 0, len = this.enemies.length; i < len; i++) {
      const enemy = this.pools.find((v) => v.enemyType === this.enemies[i]);
      if (enemy) {
        enemy.enabled = true;
      } else {
        this.pools.push(new EnemyGroup(this.scene, this.enemies[i]));
      }
    }

    // Count occurrences of each boss type for efficient duplicate handling
    const bossTypeCounts = new Map<EnemyType, number>();
    for (let i = 0, len = this.bosses.length; i < len; i++) {
      const bossType = this.bosses[i];
      bossTypeCounts.set(bossType, (bossTypeCounts.get(bossType) || 0) + 1);
    }

    console.log("Boss types and counts:", Array.from(bossTypeCounts.entries()));

    // Create or update boss pools with spawn counts
    for (const [bossType, count] of bossTypeCounts) {
      const existingPool = this.bossPools.find(
        (pool) => pool.enemyType === bossType
      );
      if (existingPool) {
        existingPool.enabled = true;
        existingPool.spawnCount = count;
        console.log(
          `Updated existing pool for ${bossType} with spawn count: ${count}`
        );
      } else {
        const newPool = new EnemyGroup(this.scene, bossType) as BossPool;
        newPool.spawnCount = count;
        this.bossPools.push(newPool);
        console.log(
          `Created new pool for ${bossType} with spawn count: ${count}`
        );
      }
    }
  }

  SpawnEnemiesInOuterRect() {
    const nonFormationEnemies = Game.Core.Enemies.filter(
      (enemy) => !enemy.inFormation
    ).length;

    // Performance-based enemy limits
    const performanceConfig = getCurrentPerformanceConfig();
    const maxEnemiesForDevice = Math.min(
      this.maximum,
      performanceConfig.enemiesLimit
    );

    if (Game.Core.IsTimeStopped || nonFormationEnemies >= maxEnemiesForDevice) {
      return;
    }

    let flag = false;
    for (let i = 0; i < this.pools.length; i++) {
      if (!this.pools[i].enabled) continue;
      const rect = Phaser.Geom.Rectangle.RandomOutside(
        this.rectOuter,
        this.rectInner
      );
      const x = Game.Core.Player.x + rect.x;
      const y = Game.Core.Player.y + rect.y;
      this.pools[i].SpawnAt(x, y);
      flag = true;
    }

    if (flag) {
      this.SwarmCheck();
    }
  }

  // Optimize SwarmCheck to avoid excessive spawning
  SwarmCheck() {
    if (Game.Core.IsTimeStopped) return;

    // Count only non-formation enemies
    const nonFormationEnemies = Game.Core.Enemies.filter(
      (enemy) => !enemy.inFormation
    ).length;

    // Performance-based spawn limits
    const performanceConfig = getCurrentPerformanceConfig();
    const performanceMinimum = Math.min(
      this.minimum * this.minimumMultiplier,
      performanceConfig.enemiesLimit * 0.6 // Use 60% of max enemies as minimum
    );
    const maxEnemiesForDevice = Math.min(
      this.maximum,
      performanceConfig.enemiesLimit
    );

    // Adjust spawn rate based on performance level
    const maxSpawnsPerCheck =
      performanceConfig.animationFPS >= 60
        ? 3
        : performanceConfig.animationFPS >= 45
        ? 2
        : 1;

    const needed = Math.min(
      performanceMinimum - nonFormationEnemies,
      maxSpawnsPerCheck
    );

    for (
      let i = 0;
      i < needed && nonFormationEnemies < maxEnemiesForDevice;
      i++
    ) {
      this.SpawnEnemiesInOuterRect();
    }
  }
  SpawnAt(e, t, i, s) {
    if (Game.Core.IsTimeStopped) return null;
    if (Game.Core.Enemies.length >= this.maximum) return null;
    let n = this.pools.find((e) => e.enemyType === i),
      a;
    return n && (a = n.SpawnAt(e, t, s)), a;
  }

  private spawnFormations(formations: FormationConfig[]) {
    if (!Game.Core.formationManager) {
      console.warn("FormationManager not initialized");
      return;
    }

    for (let i = 0, len = formations.length; i < len; i++) {
      const formationConfig = formations[i];
      const randomAngle = Math.random() * Math.PI * 2;
      const distance = formationConfig.spawnDistance || 2000;

      const pos = {
        x: Game.Core.Player.x + Math.cos(randomAngle) * distance,
        y: Game.Core.Player.y + Math.sin(randomAngle) * distance,
      };

      Game.Core.formationManager.createTreasureFormation(
        pos.x,
        pos.y,
        formationConfig
      );
    }
  }

  getDeathAppearanceTime(): number {
    // Find the minute when Death appears in the current stage
    for (let i = this.stageDataArray.length - 1; i >= 0; i--) {
      const stage = this.stageDataArray[i];

      if (
        stage.bosses?.includes(EnemyType.DEATH) &&
        stage.minute !== undefined
      ) {
        return stage.minute * 60; // Convert minutes to seconds
      }
    }
    // Default to 5 minutes if Death is not found
    return 5 * 60;
  }

  spawnCaravanEvent() {
    // Trigger the default camel caravan event every 0.5 minutes
    this.stageEventManager.TriggerEvent(DEFAULT_CAMEL_CARAVAN_EVENT);
  }
}
